// core
import React, { isValidElement } from "react";

// components
import { CircleHelp } from "lucide-react";
import InfoIcon from "../../assets/icon/InfoIcon";
import { Button } from "../button";

// hooks
import useScreenSize from "../../hooks/useScreenSize/useScreenSize";

// utils
import cn from "../../utils/cn";
import { Tooltip, TooltipArrow, TooltipContent, TooltipTrigger } from "../tooltip";

type RequireAtLeastOne<T, Keys extends keyof T = keyof T> = Pick<T, Exclude<keyof T, Keys>> &
  {
    [K in Keys]: Required<Pick<T, K>> & Partial<Pick<T, Exclude<Keys, K>>>;
  }[Keys];

type NavbarBaseProps = {
  className?: string;
  title?: React.ReactNode;
  subTitle?: React.ReactNode;
  headImageURL?: string;
  headImageURLClassName?: string;
  tooltipContentClassName?: string;
  tooltipTitle?: string;
  tooltipIcon?: React.ReactNode;
  tooltipdescription?: React.ReactNode;
  tooltipDescriptionWrapperClassName?: string;
  mainButtonText?: React.ReactNode;
  mainButtonClassName?: string;
  mainButtonDisable?: boolean;
  subButtonText?: React.ReactNode;
  subButtonClassName?: string;
  subButtonDisable?: boolean;
  onMainButtonClick?: () => void;
  onSubButtonClick?: () => void;
  searchButton?: React.ReactNode;
  separatorDisable?: boolean;
};

// 👇 ใช้ utility type เพื่อบังคับว่า ต้องมีอย่างน้อย title หรือ headImageURL
export type NavbarProps = RequireAtLeastOne<NavbarBaseProps, "title">;

const Navbar = ({
  className,
  title,
  subTitle,
  headImageURL = "",
  headImageURLClassName,
  tooltipContentClassName = "w-[450px] lg:!w-[630px]",
  tooltipTitle,
  tooltipIcon,
  tooltipdescription,
  tooltipDescriptionWrapperClassName,
  mainButtonText,
  mainButtonClassName,
  mainButtonDisable = false,
  subButtonText,
  subButtonClassName,
  subButtonDisable = false,
  onMainButtonClick,
  onSubButtonClick,
  separatorDisable = false,
  searchButton
}: NavbarProps) => {
  const { isMobile, isTablet, isDesktop } = useScreenSize();
  const Icon = CircleHelp;
  const shouldShowSeparator = !separatorDisable && isValidElement(searchButton);

  return (
    <nav
      className={cn(
        "py-3 px-8 w-full h-16 bg-sus-primary-1 flex items-center justify-between",
        className
      )}
    >
      {/* left content */}
      <div className="flex items-center gap-2.5">
        {headImageURL !== "" ?
          <img src={headImageURL} alt="" className={cn("w-full h-full", headImageURLClassName)} />
        : <div className="flex flex-col">
            {isValidElement(title) ? title : <h1 className="text-white text-xl font-bold">{title}</h1>}
            {isValidElement(subTitle) ?
              subTitle
            : <h1 className="text-white text-sm font-semibold">{subTitle}</h1>}
          </div>
        }
        {tooltipTitle && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button className="text-white hover:opacity-80 ">
                <InfoIcon className="w-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent
              forceMount
              side={isDesktop ? "right" : "bottom"}
              align="start"
              avoidCollisions={false}
              className={cn(
                "bg-white border border-black p-6",
                "transition-all duration-150 ease-out origin-top top-1 left-4",
                "data-[state=closed]:opacity-0 data-[state=open]:opacity-100",
                "data-[state=closed]:scale-95 data-[state=open]:scale-100",
                { "mt-5": isDesktop },
                tooltipContentClassName
              )}
            >
              <div
                role="tooltip"
                aria-label={tooltipTitle}
                className={cn(
                  "flex flex-col gap-4 text-sm text-black text-pretty ",
                  tooltipDescriptionWrapperClassName
                )}
              >
                {tooltipTitle && (
                  <div className="flex items-center gap-3">
                    {isValidElement(tooltipIcon) ? tooltipIcon : <Icon size={32} aria-hidden="true" />}
                    <h3 className="text-xl font-bold">{tooltipTitle}</h3>
                  </div>
                )}
                {isValidElement(tooltipdescription) && tooltipdescription}
              </div>
              <TooltipArrow className="-my-px border-none fill-white" asChild>
                <svg
                  width={isDesktop ? 35 : 33}
                  height={35}
                  viewBox="0 0 40 35"
                  xmlns="http://www.w3.org/2000/svg"
                  className={cn({
                    "rotate-90 visible": isTablet || isMobile,
                    "": isDesktop
                  })}
                >
                  <path d="M0 0 L40 0 L0 35 Z" fill="white" />
                  {isDesktop ?
                    <>
                      <line x1="0" y1="40" x2="0" y2="0" stroke="black" strokeWidth="2.5" />
                      <line x1="40" y1="0" x2="0" y2="35" stroke="black" strokeWidth="1" />
                    </>
                  : <>
                      <line x1="40" y1="0" x2="0" y2="0" stroke="black" strokeWidth="2" />
                      <line x1="40" y1="0" x2="0" y2="35" stroke="black" strokeWidth="1" />
                    </>
                  }
                </svg>
              </TooltipArrow>
            </TooltipContent>
          </Tooltip>
        )}
      </div>

      {/* right content */}
      <div className="flex items-center gap-3">
        {mainButtonText && (
          <Button
            disabled={mainButtonDisable}
            className={cn("bg-sus-secondary-1 hover:bg-sus-secondary-hover", mainButtonClassName)}
            size="lg"
            onClick={onMainButtonClick}
          >
            {mainButtonText}
          </Button>
        )}
        {subButtonText && (
          <Button
            disabled={subButtonDisable}
            className={cn("bg-sus-primary-2 hover:bg-sus-primary-2-hover", subButtonClassName)}
            size="lg"
            onClick={onSubButtonClick}
          >
            {subButtonText}
          </Button>
        )}
        {shouldShowSeparator && <div role="separator" className="ml-1 w-[1px] h-10 bg-white" />}
        {isValidElement(searchButton) ? searchButton : ""}
      </div>
    </nav>
  );
};

export default React.memo(Navbar);
